import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { api } from "../../lib/api";
import { fmtFecha } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { PageHeader } from "../../components/PageHeader";
import { StatusBadge } from "../../components/StatusBadge";
import { Coincidencia, CaseActions, CaseHistory } from "../../components/screening/CaseDetail";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const TIPOS = { posible_coincidencia: "Posible coincidencia", pep_detectado: "PEP detectado", pep_declarado: "PEP declarado", revision_identidad: "Revisión de identidad", diligencia_reforzada: "Diligencia reforzada" };
const EST = { abierto: "pendiente_verificacion", pendiente_ddr: "pendiente_verificacion", pendiente_owner: "pendiente_verificacion", pendiente_cierre: "pendiente_verificacion", cerrado: "ok" };

const Detalle = ({ id, rol, onClose, onChange }) => {
  const [c, setC] = useState(null);
  const load = useCallback(() => {
    if (id) api.get(`/company/cases/${id}`).then((r) => setC(r.data));
  }, [id]);
  useEffect(() => {
    setC(null);
    load();
  }, [load]);
  const done = () => {
    load();
    onChange();
  };
  return (
    <Dialog open={!!id} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="case-detail-dialog">
        <DialogHeader>
          <DialogTitle>{c ? `${TIPOS[c.tipo]} · ${c.fuente_nombre || "Declaración del cliente"}` : "Caso"}</DialogTitle>
          <DialogDescription>{c && `Abierto ${fmtFecha(c.creado_en)} · estado ${c.estado} · cliente ${c.cliente_actual?.estado || "—"}`}</DialogDescription>
        </DialogHeader>
        {c && (
          <div className="space-y-3">
            <p className="text-sm flex flex-wrap gap-3 items-center"><Link className="underline" to={`/panel/clientes/${c.client_id}`} data-testid="case-client-link">Ver ficha del cliente</Link>
              <span>Responsable: {c.responsable?.nombre || "sin asignar"}</span>
              {["compliance_officer", "owner_oficial"].includes(rol) && c.estado !== "cerrado" && <Button size="sm" variant="outline" onClick={() => api.post(`/company/cases/${c.id}/asignar`).then(done)} data-testid="case-assign-button">Asignarme</Button>}</p>
            {c.tipo === "revision_identidad" && <p className="text-sm">El motor KYC dejó la identidad en {c.detalle?.resultado_kyc}. Registra la revisión de identidad en la ficha y luego cierra el caso.</p>}
            {c.tipo === "diligencia_reforzada" && <p className="text-sm">Requiere diligencia reforzada ({c.detalle?.obligatoria_gafi ? "envíos a país GAFI" : ""}{c.detalle?.pep ? " PEP" : ""} nivel {c.detalle?.nivel}). Registra el formulario con respaldo en la ficha (pestaña Riesgo) y luego cierra el caso.</p>}
            {c.ros_inmediato && <p className="bg-rose-600 text-white rounded px-3 py-2 text-sm font-semibold" data-testid="case-ros-badge">ROS inmediato (Circular 62, c.11). Cliente bloqueado. No informar al cliente.</p>}
            {c.tipo === "pep_declarado" && <p className="text-sm">El cliente declaró ser PEP: {c.cliente.pep_declarado?.tipo} · {c.cliente.pep_declarado?.cargo} ({c.cliente.pep_declarado?.institucion})</p>}
            {c.requiere_owner && <p className="text-xs text-amber-700">Caso PEP: exige debida diligencia reforzada del oficial y aprobación del owner (alta gerencia), Circular 62 h.4.</p>}
            {c.coincidencias.map((m) => <Coincidencia key={m.ext_id} m={m} c={c.cliente} fuente={c.fuente} />)}
            <CaseHistory c={c} />
            <CaseActions c={c} rol={rol} onDone={done} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const CasosTabla = ({ items, onOpen }) => (
  <Table>
    <TableHeader><TableRow><TableHead>Fecha</TableHead><TableHead>Cliente</TableHead><TableHead>Tipo</TableHead><TableHead>Fuente</TableHead><TableHead>Responsable</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
    <TableBody>
      {items.map((c) => (
        <TableRow key={c.id} className="cursor-pointer" onClick={() => onOpen(c.id)} data-testid={`case-row-${c.id}`}>
          <TableCell>{fmtFecha(c.creado_en)}</TableCell>
          <TableCell>{c.cliente.nombres} {c.cliente.apellido_paterno} {c.cliente.apellido_materno || ""}</TableCell>
          <TableCell>{TIPOS[c.tipo]}{c.ros_inmediato && <b className="text-rose-700"> · ROS</b>}</TableCell>
          <TableCell className="uppercase">{c.fuente || (c.tipo.startsWith("pep") ? "declaración" : "—")}</TableCell>
          <TableCell>{c.responsable?.nombre || "sin asignar"}</TableCell>
          <TableCell><StatusBadge value={EST[c.estado]} label={c.estado} /></TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const RegistroPep = ({ items }) => (
  <Table data-testid="pep-registry-table">
    <TableHeader><TableRow><TableHead>Cliente</TableHead><TableHead>Cargo</TableHead><TableHead>Estado</TableHead><TableHead>Aprobó</TableHead></TableRow></TableHeader>
    <TableBody>
      {items.map((c) => (
        <TableRow key={c.id}>
          <TableCell>{c.nombres} {c.apellido_paterno} {c.apellido_materno || ""} · {c.documento?.numero}</TableCell>
          <TableCell>{c.pep_registro.cargo} ({c.pep_registro.institucion})</TableCell>
          <TableCell>{c.pep_registro.estado}</TableCell>
          <TableCell>{c.pep_registro.oficial} / {c.pep_registro.owner} · {fmtFecha(c.pep_registro.fecha)}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

export default function Casos() {
  const { me } = useAuth();
  const [cases, setCases] = useState([]);
  const [peps, setPeps] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => {
    api.get("/company/cases").then((r) => setCases(r.data.items));
    api.get("/company/pep-registro").then((r) => setPeps(r.data.items));
    api.get("/company/alertas").then((r) => setAlertas(r.data.items.filter((a) => !a.leida)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const abiertos = cases.filter((c) => c.estado !== "cerrado");
  return (
    <div className="space-y-6">
      <PageHeader overline="Cumplimiento" title="Casos de listas y PEP" />
      {alertas.map((a) => <p key={a.id} className="bg-rose-50 border border-rose-200 text-rose-800 rounded px-3 py-2 text-sm cursor-pointer" onClick={() => setOpen(a.case_id)} data-testid={`alert-${a.id}`}>{a.titulo} · {fmtFecha(a.fecha)}</p>)}
      <Tabs defaultValue="abiertos">
        <TabsList>
          <TabsTrigger value="abiertos" data-testid="tab-cases-open">Abiertos ({abiertos.length})</TabsTrigger>
          <TabsTrigger value="todos" data-testid="tab-cases-all">Todos ({cases.length})</TabsTrigger>
          <TabsTrigger value="pep" data-testid="tab-pep-registry">Registro PEP ({peps.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="abiertos"><CasosTabla items={abiertos} onOpen={setOpen} /></TabsContent>
        <TabsContent value="todos"><CasosTabla items={cases} onOpen={setOpen} /></TabsContent>
        <TabsContent value="pep"><RegistroPep items={peps} /></TabsContent>
      </Tabs>
      <p className="text-xs text-slate-500">Datos PEP: Fuente: InfoProbidad, Consejo para la Transparencia (CC BY 4.0).</p>
      <Detalle id={open} rol={me.usuario.rol === "owner" && me.usuario.tambien_oficial ? "owner_oficial" : me.usuario.rol} onClose={() => setOpen(null)} onChange={load} />
    </div>
  );
}
